// BCM-898 · Goods Receipt Dashboard — batch table (slides 5, 34, 35). [Uday]
// Renders the 8 columns; the chevron/expand column appears ONLY when more than one
// row is present. Only one row is expanded at a time; expanding lazily loads A2
// detail (cached per batch) without refetching the list.
//   · Single-row result (item 4): auto-expands and scrolls the TABLE HEADER to the
//     top of the viewport so the filter bar above is scrolled off and the operator
//     lands on the batch table header directly.
//   · Many rows (item 3): after a user expands then collapses a batch, the view
//     returns to THAT batch (the collapsed row is scrolled back into view) instead
//     of jumping to the top of the list.
//   · Refresh while expanded (BCM-898 bug fix): the cached A2 detail for an
//     already-expanded row is dropped and reloaded whenever the row list is
//     refetched out from under it (Refresh button, a CMO/search change, or a
//     status poll) — so Refresh shows a just-saved edit instead of the stale
//     cached value a hard page reload used to be the only way to clear.
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  Typography,
} from "@material-ui/core";
import BatchRow from "./BatchRow";
import useBatchDetails from "../hooks/useBatchDetails";
import useInfiniteScrollSentinel from "../hooks/useInfiniteScrollSentinel";
import { resolvePendingAction } from "../utils/resolvePendingAction";
import { useGRFeedback } from "../context/GRFeedbackContext";

const COLUMNS = [
  "Batch Number",
  "Material Code / Description",
  "PO Number",
  "CMO",
  "Date of Receipt",
  "Batch Qty",
  "UoM",
  "View Info",
];

const BatchTable = ({
  batches,
  loading,
  loadingMore = false,
  hasMore = false,
  loadMore,
  hasFetched,
  featuresAccess,
  qualityApprovalFlowFlag,
  onRefresh,
}) => {
  // While a status-changing action's poll is running (up to STATUS_POLL_TRIES
  // refetches, GRFeedbackContext), keep the table + action box on ONE continuous
  // loading state instead of the old value — the poll exists specifically because
  // the pending action can still read as the PREVIOUS one mid-poll, so showing it
  // would be showing stale data. The updated values appear only once busy clears.
  const { busy } = useGRFeedback();
  // CMO users are view-only across the whole dashboard, but the action section is
  // still shown to them (BCM-898 follow-up) — every control inside just renders
  // disabled (ActionSection's isViewOnlyAccess already treats CMO as view-only).
  const showChevron = batches.length > 1;
  const [expandedId, setExpandedId] = useState(null);
  const { detail, detailFor, loading: detailLoading, error: detailError, load, invalidate } = useBatchDetails();
  const tableRef = useRef(null); // the whole table — used to pin its header to the top
  const rowRefs = useRef({}); // per-batch DOM row, keyed by id (item 3 re-focus)
  const [restoreScrollId, setRestoreScrollId] = useState(null);
  // The row to keep open across the next list refetch — set by handleRefreshRow
  // so an action's refresh re-renders the batch in place (see the batches effect).
  const keepExpandedIdRef = useRef(null);
  const expandedIdRef = useRef(null);
  useEffect(() => { expandedIdRef.current = expandedId; }, [expandedId]);

  const idOf = (r) => r.schrockenDeliveryScheduleBatchID;

  const expandRow = useCallback(
    (row) => {
      const id = idOf(row);
      setExpandedId(id);
      load(row);
    },
    [load]
  );

  const toggleRow = (row) => {
    const id = idOf(row);
    if (expandedId === id) {
      // Collapsing: keep the operator anchored on THIS batch (item 3) rather than
      // letting the restored full list jump the scroll position to the top.
      setExpandedId(null);
      setRestoreScrollId(id);
    } else {
      expandRow(row);
    }
  };

  // Scroll the batch table up so its header sits just below the fixed app header,
  // scrolling the CMO / search filter bar off the top (item 4). This dashboard
  // scrolls on the WINDOW (pharmaCo/layout wraps the page in a plain div and the
  // Header is position:fixed), so we scroll the window to an absolute target rather
  // than relying on Element.scrollIntoView (which is "scroll only if needed" and
  // no-ops when the table is already partly visible). Returns true once the header
  // is within a couple of px of the target — the caller retries until then because
  // the expanded row's detail loads asynchronously and keeps growing the page.
  const FIXED_HEADER_OFFSET = 84; // ~5rem fixed app header
  const scrollHeaderToTop = useCallback(() => {
    const el = tableRef.current;
    if (!el) return false;
    const rect = el.getBoundingClientRect();

    // Case A — an inner scroll container (some app shells scroll a div, not the
    // window). Scroll the nearest scrollable ancestor if one exists.
    const isScrollable = (n) => {
      if (!n || n === document.body || n === document.documentElement) return false;
      const oy = window.getComputedStyle(n).overflowY;
      return /(auto|scroll|overlay)/.test(oy) && n.scrollHeight > n.clientHeight + 1;
    };
    let sc = el.parentElement;
    while (sc && !isScrollable(sc)) sc = sc.parentElement;
    if (sc) {
      const scRect = sc.getBoundingClientRect();
      const desired = sc.scrollTop + (rect.top - scRect.top) - 8; // small gap
      const maxScroll = Math.max(0, sc.scrollHeight - sc.clientHeight);
      sc.scrollTo({ top: Math.max(0, Math.min(desired, maxScroll)), behavior: "auto" });
      return maxScroll >= desired - 2; // done only once tall enough to reach it
    }

    // Case B — the WINDOW scrolls (this dashboard: fixed header + plain content
    // div in pharmaCo/layout). Scroll to an absolute page offset.
    const scroller = document.scrollingElement || document.documentElement;
    const current = window.pageYOffset || scroller.scrollTop || 0;
    const target = Math.max(0, Math.round(current + rect.top - FIXED_HEADER_OFFSET));
    if (target <= 2) return true; // header already at the top — nothing to do
    const maxScroll = Math.max(0, scroller.scrollHeight - window.innerHeight);
    window.scrollTo({ top: Math.min(target, maxScroll), behavior: "auto" });
    // Only "done" once the page is tall enough for the header to actually REACH the
    // target — otherwise keep retrying as the async detail grows the page. (The old
    // bug: when the page was still short, clamped collapsed to 0 == current and this
    // reported done on the first try, so it never scrolled once content loaded.)
    return maxScroll >= target - 2;
  }, []);

  // Auto-expand when exactly one row is returned, then keep nudging the header to
  // the top until the async detail has grown the page enough to get there (item 4).
  // A view-only row — whether CMO (view-only dashboard-wide) or a BMS user who
  // lacks permission for this specific pending action — still auto-expands
  // (BCM-898 follow-up); it just renders read-only.
  useEffect(() => {
    // A list (re)fetch — action-driven or the Refresh button — can change any
    // batch's detail, so drop the cache: the expand below re-reads it.
    invalidate();
    const keepId = keepExpandedIdRef.current;
    keepExpandedIdRef.current = null;
    if (batches.length !== 1) {
      // After an action's refresh, keep the batch the user was working on open so
      // its updated pending action shows in place; any other list change collapses.
      const keepRow = keepId != null ? batches.find((r) => idOf(r) === keepId) : null;
      if (keepRow) expandRow(keepRow);
      else setExpandedId(null);
      return;
    }
    expandRow(batches[0]);
    let tries = 0;
    const timers = [];
    const attempt = () => {
      const done = scrollHeaderToTop();
      tries += 1;
      if (!done && tries < 25) timers.push(setTimeout(attempt, 150)); // ~3.75s window
    };
    timers.push(setTimeout(attempt, 80));
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batches]);

  // BCM-898 bug fix: the per-batch A2 detail is cached (useBatchDetails) so
  // re-expanding the SAME row doesn't refetch — correct for a plain toggle,
  // but when the ROW LIST itself is refetched (the FilterBar "Refresh" button)
  // while a row stays expanded, that cache goes stale. expandRow()/load() just
  // re-serves the cache in that case, so a user who saved a field edit and
  // clicked Refresh kept seeing the pre-edit value — only a hard page reload
  // actually called fetchDeliveryScheduleBatchDetails again. Whenever the list
  // changes under an already-expanded row, drop its cached detail and reload
  // it fresh. Purely additive: a plain expand/collapse click never changes
  // `batches`, so this never fires for that — only for an actual list refetch
  // (Refresh, a CMO/search change, or GRFeedbackContext's own poll).
  useEffect(() => {
    if (expandedId == null) return;
    const row = batches.find((r) => idOf(r) === expandedId);
    if (!row) return; // no longer in the list — the auto-expand effect above handles collapsing it
    invalidate(expandedId);
    load(row);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batches]);

  // After a collapse restores the full list, bring the just-collapsed row back
  // into view so the user stays on the batch they were working on (item 3).
  useEffect(() => {
    if (restoreScrollId != null && expandedId == null) {
      const el = rowRefs.current[restoreScrollId];
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      setRestoreScrollId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoreScrollId, expandedId]);

  // Refresh the row + drop its cached detail after an action, then re-run the list,
  // keeping the currently expanded row open through it. Returns the refetch promise
  // (resolving to the rows) so the feedback layer can poll for a status change
  // after a status-affecting action.
  const handleRefreshRow = useCallback(() => {
    invalidate();
    keepExpandedIdRef.current = expandedIdRef.current;
    return onRefresh ? onRefresh() : Promise.resolve([]);
  }, [invalidate, onRefresh]);

  const totalCols = COLUMNS.length + (showChevron ? 1 : 0);

  // When multiple batches are present and one is expanded, show ONLY that batch;
  // the others are hidden until it is collapsed again (chevron toggles back).
  const visibleBatches =
    showChevron && expandedId != null
      ? batches.filter((row) => idOf(row) === expandedId)
      : batches;

  // Infinite-scroll sentinel: only observe when the full list is on screen
  // (an expanded row hides everything else — the sentinel would sit right in the
  // viewport and needlessly fire a page fetch that the user can't see yet),
  // and never while a page-1 refetch or the post-action status poll is running.
  const enableSentinel =
    hasFetched &&
    !loading &&
    !busy &&
    !loadingMore &&
    hasMore &&
    expandedId == null &&
    batches.length > 0 &&
    typeof loadMore === "function";
  const sentinelRef = useInfiniteScrollSentinel({
    enabled: enableSentinel,
    onReach: loadMore,
  });

  // The very first fetch after a CMO is picked / a search is run has not resolved
  // yet, so there is no table to draw — tell the user it is loading instead of
  // leaving the area blank.
  if (!hasFetched) {
    if (!loading) return null; // landing state handled by the container
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        p={5}
      >
        <CircularProgress size={30} />
        <Typography variant="body2" style={{ color: "#6B7280", marginTop: 12 }}>
          Loading batches…
        </Typography>
      </Box>
    );
  }

  return (
    <>
    <TableContainer ref={tableRef} component={Paper} style={{ boxShadow: "none", border: "1px solid #C6C6C6" }}>
      <Table aria-label="goods receipt batches">
        <TableHead>
          <TableRow>
            {showChevron && (
              <TableCell style={{ fontWeight: 700, backgroundColor: "#F3F5F8" }}>Action</TableCell>
            )}
            {COLUMNS.map((c) => (
              <TableCell
                key={c}
                align={
                  c === "Batch Qty" || c === "UoM"
                    ? "right"
                    : c === "PO Number" || c === "Date of Receipt" || c === "View Info"
                    ? "center"
                    : "left"
                }
                style={{ fontWeight: 700, verticalAlign: "top", backgroundColor: "#F3F5F8" }}
              >
                {c}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Two distinct loading states, deliberately handled differently:
              · True first load (nothing to show yet) — a normal one-shot spinner.
              · `busy` (the up-to-4x status-change poll in GRFeedbackContext) — ONE
                continuous loading state for the table AND action box for as long as
                the poll runs. Each individual refetch inside that poll still flips
                the plain `loading` prop, but we deliberately do NOT key off that
                here — reacting to it would blank-and-refill the body on every one
                of the up to 4 tries ("reloading again and again"), and would reveal
                the STALE pre-poll values in between tries (the poll exists exactly
                because the pending action can still read as the previous one mid-
                poll). Gating on `busy` instead shows loading once, continuously,
                and only reveals the settled values when the poll actually
                finishes — win or exhausted. */}
          {(loading ) || busy ? (
            <TableRow>
              <TableCell colSpan={totalCols}>
                <Box display="flex" alignItems="center" justifyContent="center" p={2}>
                  <CircularProgress size={22} />
                  <Typography variant="body2" style={{ color: "#6B7280", marginLeft: 12 }}>
                    {busy ? "Updating batch…" : "Loading batches…"}
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : batches.length === 0 ? (
            <TableRow>
              <TableCell colSpan={totalCols} align="center" style={{ color: "#777" }}>
                No received batches found.
              </TableCell>
            </TableRow>
          ) : (
            visibleBatches.map((row) => {
              const id = idOf(row);
              const isExpanded = expandedId === id;
              // Same value BatchRow gets as `expanded` (a lone batch is always open).
              const rowExpanded = showChevron ? isExpanded : true;
              // Bind the loaded detail to the row it belongs to by IDENTITY, not by
              // isExpanded. While a row collapses (expandedId → null) its detail is
              // still `detailFor`, so the resolved action stays stable through the
              // close animation instead of briefly flashing the no-detail best guess
              // (the "glimpse of another section" on collapse).
              const ownsDetail = detailFor === id;
              const rowDetail = ownsDetail ? detail : null;
              const rowDetailLoading = ownsDetail ? detailLoading : false;
              const rowDetailError = ownsDetail ? detailError : null;
              // `expanded` only affects PA2's label: a collapsed row reads "Review
              // pending" even though its detail is still held (see above).
              const { label } = resolvePendingAction(row, rowDetail || undefined, { expanded: rowExpanded });
              return (
                <BatchRow
                  key={id}
                  row={row}
                  showChevron={showChevron}
                  expanded={rowExpanded}
                  onToggle={() => toggleRow(row)}
                  pendingLabel={label}
                  detail={rowDetail}
                  detailLoading={rowDetailLoading}
                  detailError={rowDetailError}
                  featuresAccess={featuresAccess}
                  qualityApprovalFlowFlag={qualityApprovalFlowFlag}
                  onRefresh={handleRefreshRow}
                  rowRef={(el) => {
                    if (el) rowRefs.current[id] = el;
                    else delete rowRefs.current[id];
                  }}
                  colSpan={totalCols}
                />
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
    {/* Paging sentinel — an invisible marker below the table. When it scrolls
        into the viewport (with a ~200px lead so the next page arrives before the
        user hits the bottom), useInfiniteScrollSentinel calls loadMore(). We
        always mount the sentinel and gate observation with `enableSentinel`
        above, so React never has to re-attach the ref mid-scroll. */}
    <div ref={sentinelRef} aria-hidden style={{ height: 1 }} />
    {loadingMore && (
      <Box display="flex" alignItems="center" justifyContent="center" p={2}>
        <CircularProgress size={22} />
        <Typography variant="body2" style={{ color: "#6B7280", marginLeft: 12 }}>
          Loading more batches…
        </Typography>
      </Box>
    )}
    {!hasMore && !loading && !loadingMore && batches.length >= 20 && (
      <Box display="flex" justifyContent="center" p={2}>
        <Typography variant="caption" style={{ color: "#9CA3AF" }}>
          End of results
        </Typography>
      </Box>
    )}
    </>
  );
};

export default BatchTable;
